/**
 * Tokyo Metro — action card deck.
 *
 * Real game: 54 cards in 6 column-pairs of 9 each. Each card has 1
 * or more action spots with a cost and yen/disc requirement. For a
 * playable app-sized v1, we generate 54 deterministic cards that
 * cover the 12 action types with varying costs. No shuffling bias
 * baked in — the server shuffles at room start.
 *
 * When the real deck is available, replace `TOKYO_METRO_DECK` with
 * the authoritative list; nothing else in the game logic needs to
 * change.
 */

export type ActionType =
  | 'MOVE'       // move meeple +N spaces
  | 'STATION'    // build station pyramid at your meeple
  | 'INVEST'     // buy stock in a train line
  | 'START'      // start a train line running
  | 'SPEED'      // add speed token to a train line
  | 'BIKE'       // take a bicycle token
  | 'DISCOUNT'   // pre-pay a discount for a future action
  | 'BIDDING'    // pre-commit yen to next round's turn-order bid
  | 'LOAN'       // take or repay a 1000-yen loan
  | 'SPECULATE'  // bet yen on a train line's final income
  | 'ACTION'     // gain another action disc (max 6)
  | 'CARD'       // claim a face-up action card

export interface ActionSlot {
  /** Unique ID within the card (0, 1, or 2). */
  slot: number
  type: ActionType
  /** Yen cost paid to the bank to perform this action. */
  cost: number
  /** Number of action discs required (1 = normal, 2 = stronger action). */
  discs: number
  /**
   * Optional parameter that clarifies the action magnitude.
   *   MOVE:   value = extra spaces (1–2)
   *   INVEST: value = line stock multiplier bump
   *   SPEED:  value = token count
   */
  value?: number
}

export interface ActionCardDef {
  /** Stable id so the DB can reference a card without storing the full object. */
  id: string
  /** Column pair index 0–5 (G/M, H/T, C/Y, Z/N, A/I, S/E). */
  column: 0 | 1 | 2 | 3 | 4 | 5
  /** Letter pair label for display. */
  columnLabel: string
  slots: ActionSlot[]
}

// ─── Deck construction ────────────────────────────────────────────────
// 9 cards per column × 6 columns = 54. Each column gets:
//   3 cards with 1 dot (single-slot, cheap)
//   3 cards with 2 dots (single-slot, medium)
//   3 cards with 3 dots (multi-slot, rich actions)
//
// Across all 54 we distribute the 12 action types roughly evenly so
// every action can come up during a game.

const COLUMN_LABELS = ['G/M', 'H/T', 'C/Y', 'Z/N', 'A/I', 'S/E'] as const

/**
 * Rotating menu of action templates we stamp onto cards. Ordered so
 * common actions (MOVE, STATION, INVEST) appear more often than the
 * edge-case ones (LOAN, CARD, SPECULATE).
 */
const ACTION_TEMPLATES: Array<Omit<ActionSlot, 'slot'>> = [
  { type: 'MOVE',      cost: 0,   discs: 1, value: 1 },
  { type: 'STATION',   cost: 200, discs: 1 },
  { type: 'INVEST',    cost: 300, discs: 1 },
  { type: 'START',     cost: 100, discs: 1 },
  { type: 'SPEED',     cost: 100, discs: 1, value: 1 },
  { type: 'BIKE',      cost: 0,   discs: 1 },
  { type: 'MOVE',      cost: 0,   discs: 1, value: 2 },
  { type: 'STATION',   cost: 500, discs: 1 },
  { type: 'INVEST',    cost: 500, discs: 1 },
  { type: 'DISCOUNT',  cost: 100, discs: 1, value: 100 },
  { type: 'ACTION',    cost: 500, discs: 2 },
  { type: 'BIDDING',   cost: 100, discs: 1, value: 100 },
  { type: 'LOAN',      cost: 0,   discs: 1 },
  { type: 'SPECULATE', cost: 0,   discs: 2 },
  { type: 'CARD',      cost: 300, discs: 2 },
]

function buildDeck(): ActionCardDef[] {
  const cards: ActionCardDef[] = []
  let tmplIdx = 0
  for (let col = 0; col < 6; col++) {
    // 3 single-slot cards per column
    for (let i = 0; i < 9; i++) {
      const slots: ActionSlot[] = []
      if (i < 3) {
        // simple: 1 slot, 1 disc
        slots.push({ slot: 0, ...ACTION_TEMPLATES[tmplIdx % ACTION_TEMPLATES.length] })
        tmplIdx++
      } else if (i < 6) {
        // medium: 1 slot, could require 2 discs
        const t = ACTION_TEMPLATES[tmplIdx % ACTION_TEMPLATES.length]
        slots.push({ slot: 0, ...t, discs: Math.max(1, t.discs) })
        tmplIdx++
      } else {
        // rich: 2 slots on the same card — multiple players can use
        slots.push({ slot: 0, ...ACTION_TEMPLATES[tmplIdx % ACTION_TEMPLATES.length] })
        tmplIdx++
        slots.push({ slot: 1, ...ACTION_TEMPLATES[tmplIdx % ACTION_TEMPLATES.length] })
        tmplIdx++
      }
      cards.push({
        id: `c${col}-${i}`,
        column: col as ActionCardDef['column'],
        columnLabel: COLUMN_LABELS[col],
        slots,
      })
    }
  }
  return cards
}

export const TOKYO_METRO_DECK: ActionCardDef[] = buildDeck()

/** Fast lookup from card id → def. */
export const CARD_BY_ID: Record<string, ActionCardDef> = TOKYO_METRO_DECK.reduce(
  (acc, c) => { acc[c.id] = c; return acc },
  {} as Record<string, ActionCardDef>,
)

// ─── Real card art mapping ────────────────────────────────────────────
// The user shipped 168 PNGs in `public/games/tokyo-metro/cards/` named
// `V2 TOKYO METRO MINI CARDS PRINT-{NN}.webp` (zero-padded to 2 digits
// for 1–99, 3 digits for 100+). Pairs of consecutive numbers are the
// front + back of one card:
//   * Cards 1–36 (images 01–72) are stock cards (12 lines × 3 share
//     orders).
//   * Cards 37–90 (images 73–180) are the 54 action cards laid out
//     6 columns × 9 cards each. Front shows the action slots, back
//     shows the column-pair-coloured generic back.
//
// A handful of front images are missing from the scan set — the
// helpers below fall back to the back image (which still tells you
// the column) and the UI shows a styled placeholder over it.

const MISSING_CARD_IMAGES = new Set<number>([
  35, 43, 48, 89, 99, 120, 131, 134, 138, 152, 177, 179,
])
const CARD_IMAGE_BASE = '/games/tokyo-metro/cards/V2 TOKYO METRO MINI CARDS PRINT-'

function imageNumberToPath(n: number): string {
  // 1–99 → 2-digit padding; 100+ → 3-digit; matches the scanned files.
  const padded = n < 100 ? String(n).padStart(2, '0') : String(n).padStart(3, '0')
  return `${CARD_IMAGE_BASE}${padded}.webp`
}

/** Parse `c{col}-{idx}` → real action-card image numbers. */
function imageNumbersForCardId(cardId: string): { front: number; back: number } | null {
  const match = /^c(\d)-(\d+)$/.exec(cardId)
  if (!match) return null
  const col = parseInt(match[1], 10)
  const idx = parseInt(match[2], 10)
  if (col < 0 || col > 5 || idx < 0 || idx > 8) return null
  // Action cards start at image 73 (= card 37). Card N → images
  // (2N-1) front + (2N) back. Per-column block of 18 images (9 cards
  // × 2 sides).
  const front = 73 + col * 18 + idx * 2
  return { front, back: front + 1 }
}

export interface CardArt {
  /** Absolute path to the front PNG; null if the front scan is missing. */
  front: string | null
  /** Absolute path to the back PNG (column-pair coloured generic back). */
  back: string | null
}

/** Resolve a logical card id to its real PNG asset paths. */
export function cardArtFor(cardId: string): CardArt {
  const nums = imageNumbersForCardId(cardId)
  if (!nums) return { front: null, back: null }
  return {
    front: MISSING_CARD_IMAGES.has(nums.front) ? null : imageNumberToPath(nums.front),
    back:  MISSING_CARD_IMAGES.has(nums.back)  ? null : imageNumberToPath(nums.back),
  }
}

/**
 * Stock card art helpers. Cards 1–36 are stock cards; the order in
 * the scanned PNGs is line G→M→H→T→C→Y→Z→N→A→I→S→E for the 1st
 * share, then the same line order for the 2nd share, then 3rd.
 */
const STOCK_LINE_ORDER = ['G','M','H','T','C','Y','Z','N','A','I','S','E'] as const

export function stockCardArt(line: string, order: 1 | 2 | 3): CardArt {
  const lineIdx = STOCK_LINE_ORDER.indexOf(line as typeof STOCK_LINE_ORDER[number])
  if (lineIdx < 0) return { front: null, back: null }
  const cardN = (order - 1) * 12 + lineIdx + 1   // 1..36
  const front = cardN * 2 - 1
  const back  = cardN * 2
  return {
    front: MISSING_CARD_IMAGES.has(front) ? null : imageNumberToPath(front),
    back:  MISSING_CARD_IMAGES.has(back)  ? null : imageNumberToPath(back),
  }
}
